/* eslint-disable no-empty */
/**
 * Capacitor + WebView authentication bridge.
 *
 * Why this file exists:
 * - Google OAuth (accounts.google.com) silently rejects requests from Android
 *   WebViews — even with a clean User-Agent — because of FedCM / Sec-CH-UA /
 *   storage-access fingerprinting. The result is either "Error 403:
 *   disallowed_useragent" or a "400. That's an error." after the user
 *   submits credentials. Routing OAuth through Chrome Custom Tabs (real
 *   Chrome) avoids every one of those checks.
 * - Custom Tabs and the Capacitor WebView maintain SEPARATE cookie jars, so
 *   the session cookie set inside Custom Tabs is invisible to the WebView.
 *   We solve this with a one-time fragment-token: the server passes the
 *   session_token in the URL fragment of the deep-link redirect; Android's
 *   App Links handler (autoVerify=true on astrokuberchat.com) closes the
 *   Custom Tab and re-opens our app with that URL, the `appUrlOpen` listener
 *   below extracts the fragment, POSTs the token to
 *   /api/auth/google/exchange, and receives the real HttpOnly cookie.
 *
 * Public surface:
 * - `startGoogleSignIn(nextUrl)` — drop-in replacement for the old
 *   `window.location.href = '/api/auth/google/start?next=...'` pattern.
 *   On Web it does exactly that. Inside the Capacitor app it opens the
 *   start URL in Chrome Custom Tabs via @capacitor/browser, with `app=1`.
 * - `initCapacitorAuthBridge()` — call once on app boot. Subscribes to
 *   Capacitor's `appUrlOpen` event (App Links re-entry) and also drains
 *   any `#capacitor_session=...` already present in the current URL
 *   (e.g. when the deep-link launched the app from cold start).
 */

const HASH_KEY = "capacitor_session";

function isCapacitor() {
  if (typeof window === "undefined") return false;
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

/**
 * Kick off Google OAuth. Use this everywhere instead of `window.location.href`.
 * - Web: native redirect (preserves the existing behaviour exactly).
 * - App (Android): opens a Chrome-hosted Custom Tab via our native
 *   ChromeAuthTabPlugin so Samsung Internet cannot become the tab host
 *   (its mailto: hijack turns account picker taps into Gmail compose
 *   — see the plugin file for the whole story).
 * - App (iOS): opens Chrome/Safari Custom Tab via @capacitor/browser.
 */
export async function startGoogleSignIn(nextUrl = "/") {
  const next = encodeURIComponent(nextUrl || "/");
  if (!isCapacitor()) {
    window.location.href = `/api/auth/google/start?next=${next}`;
    return;
  }
  // Build absolute URL — Custom Tabs needs an absolute https:// URL.
  const origin = window.location.origin;
  const startUrl = `${origin}/api/auth/google/start?next=${next}&app=1`;

  // Android path: use our native plugin that pins the Custom Tab to
  // real Chrome. This is the only reliable way to avoid the Samsung
  // OneUI mailto-hijack (tap on account row → Gmail opens). iOS falls
  // through to @capacitor/browser below.
  try {
    const platform = window.Capacitor?.getPlatform?.();
    if (platform === "android") {
      const { registerPlugin } = await import("@capacitor/core");
      const ChromeAuthTab = registerPlugin("ChromeAuthTab");
      await ChromeAuthTab.open({ url: startUrl });
      return;
    }
  } catch (err) {
    console.warn("ChromeAuthTab unavailable, falling back to @capacitor/browser:", err);
    // Fall through to the generic browser open below.
  }

  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({
      url: startUrl,
      // System default Chrome Custom Tab — real Chrome, no WebView
      // fingerprint, no disallowed_useragent.
      windowName: "_self",
      presentationStyle: "fullscreen",
    });
  } catch (err) {
    // If the Browser plugin somehow isn't available, fall back to the
    // native location change so the user isn't stranded (they may hit
    // the WebView OAuth error, but they're no worse off than before).
    console.warn("Capacitor Browser unavailable, falling back to in-WebView OAuth:", err);
    window.location.href = startUrl;
  }
}

/**
 * Given a URL string, extract the capacitor_session fragment token (if any).
 */
function extractToken(urlStr) {
  if (!urlStr || typeof urlStr !== "string") return null;

  // Newer flow (v2.0.9+): backend redirects Chrome Custom Tab to
  //   astrokuberchatapp://auth?session=xxx&next=/
  // Chrome dispatches the custom scheme back to the app which fires
  // appUrlOpen; the token lives in the query string, not the fragment.
  try {
    if (urlStr.startsWith("astrokuberchatapp://")) {
      const u = new URL(urlStr);
      const t = u.searchParams.get("session");
      if (t) return t;
    }
  } catch {}

  // Legacy flow: astrokuberchat.com/#capacitor_session=xxx (still used
  // by web signups where the browser handles the redirect itself).
  const hashIdx = urlStr.indexOf("#");
  if (hashIdx < 0) return null;
  const hash = urlStr.slice(hashIdx + 1);
  if (!hash.includes(HASH_KEY)) return null;
  const params = new URLSearchParams(hash);
  const token = params.get(HASH_KEY);
  return token || null;
}

/** Return the post-login navigation target embedded in a custom-scheme URL. */
function extractNextPath(urlStr) {
  try {
    if (urlStr && urlStr.startsWith("astrokuberchatapp://")) {
      const u = new URL(urlStr);
      const next = u.searchParams.get("next");
      if (next) return next;
    }
  } catch {}
  return null;
}

/**
 * Exchange a fragment token for the HttpOnly session cookie.
 * Returns true if exchange succeeded.
 */
async function exchangeToken(token) {
  try {
    const resp = await fetch("/api/auth/google/exchange", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    });
    if (!resp.ok) {
      console.warn("Capacitor session exchange failed:", resp.status);
      return false;
    }
    return true;
  } catch (e) {
    console.warn("Capacitor session exchange error:", e);
    return false;
  }
}

/**
 * Process a deep-link URL: extract token, exchange for cookie, then
 * navigate the WebView to the clean URL (no fragment) so AuthContext
 * picks up the new session.
 */
async function processDeepLink(urlStr) {
  const token = extractToken(urlStr);
  if (!token) return false;
  const ok = await exchangeToken(token);
  // Try to close the Chrome Custom Tab regardless of outcome — the user
  // is back in the app at this point.
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {}
  if (!ok) return false;
  // Navigate to the requested destination.
  //  - Custom-scheme URLs (astrokuberchatapp://auth?next=/x) carry the
  //    target path in the `next` query param.
  //  - Legacy astrokuberchat.com URLs carry it in the pathname + search
  //    (with the token in the fragment we already stripped).
  try {
    const nextFromScheme = extractNextPath(urlStr);
    if (nextFromScheme) {
      // Force a full reload so AuthContext re-runs /auth/me with the
      // new cookie.
      window.location.replace(nextFromScheme || "/");
      return true;
    }
    const u = new URL(urlStr);
    const cleanPath = u.pathname + u.search;
    window.location.replace(cleanPath || "/");
  } catch {
    window.location.replace("/");
  }
  return true;
}

/**
 * Wire up Capacitor's `appUrlOpen` listener so deep-link re-entries are
 * processed even when the app is already running. Also drain any
 * `#capacitor_session=...` already present in the current URL (cold
 * start via deep link).
 *
 * Safe to call on Web — it no-ops outside Capacitor.
 */
export async function initCapacitorAuthBridge() {
  if (typeof window === "undefined") return;

  // 1) Cold-start: the URL we booted with already carries the fragment.
  try {
    const initial = window.location.href || "";
    if (extractToken(initial)) {
      await processDeepLink(initial);
      // processDeepLink may have triggered a reload; if not, fall through.
    }
  } catch {}

  if (!isCapacitor()) return;

  // 2) Warm-start: App Links re-enter while the app is alive.
  //
  // When the Custom Tab redirects from accounts.google.com back to
  // astrokuberchat.com/auth/google?code=..., Android's App Links verifier
  // (autoVerify=true in AndroidManifest.xml) intercepts the URL, closes
  // the Custom Tab, and re-opens our Capacitor app with that URL via the
  // appUrlOpen event. The WebView is still on whatever page it was on
  // when the user clicked "Sign in", so we have to manually navigate it
  // to the deep-link URL — the GoogleAuthCallback redirector + backend
  // /auth/google/callback then complete the flow and 302 us back to
  // `/#capacitor_session=...`, where the cold-start drain (re-fired by
  // the full-page reload) takes over.
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("appUrlOpen", async ({ url }) => {
      try {
        if (!url) return;
        // If the URL already carries the fragment (e.g. cold-start race
        // or future server changes), drain it directly.
        if (extractToken(url)) {
          await processDeepLink(url);
          return;
        }
        // Otherwise navigate the WebView to the deep link so the rest
        // of the OAuth flow can run in-app.
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch {}
        try {
          const u = new URL(url);
          const inAppPath = u.pathname + u.search + u.hash;
          window.location.replace(inAppPath || "/");
        } catch {
          // If URL parsing fails, do nothing — user is back in the app.
        }
      } catch (e) {
        console.warn("appUrlOpen handler error:", e);
      }
    });
  } catch (e) {
    console.warn("Could not register Capacitor appUrlOpen listener:", e);
  }
}
